import OneAccountIcon from '@/components/icons/OneAccountIcon';
import {
  closeBankAccountService,
  depositSweepEnrollService,
  depositSweepUnenrollService,
  fileTaxDocumentService,
  getBankAccountDetailsService,
  getBankAccountsList,
  getStatementService,
  getTaxDocumentsService,
  lockBankAccountService,
  revealBankAccountNumberService,
  unlockBankAccountService,
  updateBankAccountService,
} from '@/services/bankAccounts';
import {
  BankAccountEditFormValues,
  BankAccountsListQueryParams,
  LockReasonsFormValues,
  queryKeys,
} from '@/types/bankAccounts';
import { TransactionRequestSelectionItem } from '@/types/transactionRequests';
import { formatCurrency } from '@/utils/currency';
import { convertObjectValuesToString } from '@/utils/responseConvertors';
import {
  QueryClient,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { useAuthUser } from '../stores/useAuthUser';
import useIdempotencyKeyGenerator from '../useIdempotencyKeyGenerator';

function clearQueryCache(queryClient: QueryClient) {
  queryClient.invalidateQueries({
    queryKey: [queryKeys.list],
  });
  queryClient.invalidateQueries({
    queryKey: [queryKeys.details],
  });
}

export const useGetBankAccountsList = (
  queryParams?: BankAccountsListQueryParams,
  enabled = true,
) => {
  const [searchParams] = useSearchParams();
  const params = queryParams
    ? new URLSearchParams(convertObjectValuesToString(queryParams)).toString()
    : searchParams.toString();

  const { data, isPending, isSuccess } = useQuery({
    queryKey: [queryKeys.list, params],
    queryFn: () => getBankAccountsList(params),
    enabled,
  });

  const accountsForSelection: TransactionRequestSelectionItem[] = data
    ? data.bankAccounts.records.map(account => {
        return {
          value: account.uuid,
          label: `[${account.accountTypeStr}] ...${account.accountNumberLast4}`,
          icon: OneAccountIcon,
          isInternalAccount: true,
          availableBalance: account.availableBalance,
          additionalInfo: formatCurrency({
            amount: account.availableBalance.value,
          }),
          callerIsAuthorizedTeammate: account.callerIsAuthorizedTeammate,
        };
      })
    : [];

  return {
    isSuccess,
    accounts: data?.bankAccounts?.records || [],
    accountsForSelection,
    aggregateInfo: data?.aggregateInfo,
    totalRecordCount: data?.bankAccounts.totalRecordCount || 0,
    accountsIsPending: isPending,
  };
};

export const useGetBankAccountDetails = (uuid: string) => {
  const { data, isPending } = useQuery({
    queryKey: [queryKeys.details, uuid],
    queryFn: () => getBankAccountDetailsService(uuid),
    enabled: !!uuid,
  });

  return {
    accountDetails: data,
    accountsDetailsIsPending: isPending,
  };
};

export const useUpdateBankAccount = () => {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending } = useMutation({
    mutationFn: (data: BankAccountEditFormValues) =>
      updateBankAccountService(data),
    onSuccess: () => clearQueryCache(queryClient),
  });

  return {
    updateBankAccount: mutateAsync,
    isUpdateBankAccountPending: isPending,
  };
};

export const useRevealBankAccountNumber = (uuid: string) => {
  const { mutateAsync, isPending } = useMutation({
    mutationFn: () => revealBankAccountNumberService(uuid),
  });

  return {
    revealAccountNumber: mutateAsync,
    isRevealAccountNumberPending: isPending,
  };
};

export const useLockBankAccount = () => {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending } = useMutation({
    mutationFn: (data: LockReasonsFormValues) => lockBankAccountService(data),
    onSuccess: () => clearQueryCache(queryClient),
  });

  return {
    lockBankAccount: mutateAsync,
    isLockBankAccountPending: isPending,
  };
};

export const useUnlockBankAccount = (uuid: string) => {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending } = useMutation({
    mutationFn: () => unlockBankAccountService(uuid),
    onSuccess: () => clearQueryCache(queryClient),
  });

  return {
    unlockBankAccount: mutateAsync,
    isUnlockBankAccountPending: isPending,
  };
};

export const useCloseBankAccount = (uuid: string, enabled = false) => {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending } = useMutation({
    mutationFn: () => closeBankAccountService(uuid, false),
    onSuccess: () => clearQueryCache(queryClient),
  });
  const { data, isFetching: isCanCloseBankAccountPending } = useQuery({
    queryKey: [queryKeys.preflight, uuid],
    queryFn: () => closeBankAccountService(uuid, true),
    enabled,
  });

  return {
    canCloseBankAccount: data?.canClose || false,
    isCanCloseBankAccountPending,
    cloesBankAccount: mutateAsync,
    isCloseBankAccountPending: isPending,
  };
};

export const useGetStatements = (uuid: string) => {
  const { data, isPending } = useQuery({
    queryKey: [queryKeys.statements, uuid],
    queryFn: () => getStatementService(uuid),
    gcTime: Infinity,
    staleTime: Infinity,
  });

  return {
    statements: data?.records || [],
    totalRecordCount: data?.totalRecordCount || 0,
    statementsIsPending: isPending,
  };
};

export const useGetTaxDocuments = (uuid: string) => {
  const { isStaff } = useAuthUser();
  const { data, isPending } = useQuery({
    queryKey: [queryKeys.taxes, uuid],
    queryFn: () => getTaxDocumentsService(uuid, isStaff),
    gcTime: Infinity,
    staleTime: Infinity,
  });

  return {
    taxDocuments: data?.records || [],
    totalRecordCount: data?.totalRecordCount,
    taxDocumentsIsPending: isPending,
  };
};

export const useFileTaxDocument = (uuid: string) => {
  const queryClient = useQueryClient();

  const { mutateAsync, isPending } = useMutation({
    mutationFn: () => fileTaxDocumentService(uuid),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.taxes],
      });
    },
  });

  return {
    fileTaxDocument: mutateAsync,
    isFileTaxDocumentPending: isPending,
  };
};

export const useDepositSweep = (uuid: string) => {
  const idempotencyKey = useIdempotencyKeyGenerator();
  const queryClient = useQueryClient();

  const {
    mutateAsync: depositSweepEnroll,
    isPending: depositSweepsEnrollIsPending,
  } = useMutation({
    mutationFn: () => depositSweepEnrollService(uuid, idempotencyKey),
    onSuccess: () => clearQueryCache(queryClient),
  });

  const {
    mutateAsync: depositSweepUneroll,
    isPending: depositSweepsUnenrollIsPending,
  } = useMutation({
    mutationFn: () => depositSweepUnenrollService(uuid, idempotencyKey),
    onSuccess: () => clearQueryCache(queryClient),
  });

  return {
    depositSweepEnroll,
    depositSweepUneroll,
    isDepositSweepsPending:
      depositSweepsEnrollIsPending || depositSweepsUnenrollIsPending,
  };
};
